import { VenueId } from '@beat-venues/common-types';
import { useTranslation } from 'next-i18next';
import { type VariantProps, tv } from 'tailwind-variants';

import {
    ChevronRightSolidIcon,
    Container,
    Heading,
    InputField,
    Logo,
} from '../../atoms';
import type { CommonProps } from '../../types';

import type { JSX } from 'react';

export interface NewsletterProps
    extends CommonProps,
        VariantProps<typeof NewsletterStyles> {
    venue: VenueId;
    onSubmit: (value: string) => void;
    customTranslation?: boolean;
    customTranslationMessage?: string;
    customTranslationPlaceholder?: string;
}

export const NewsletterStyles = tv({
    slots: {
        base: 'mx-auto py-10 lg:py-12 border-t-brand-500 border-t-2 bg-center',
        logo: 'mx-auto',
        heading: 'text-center',
        form: 'mx-auto max-w-[22.75rem]',
        container: 'flex flex-col gap-9',
    },
    variants: {
        venueType: {
            beat: {
                base: 'bg-[url(/assets/beat/newsletter/background.jpg)]',
            },
            sportpaleis: {
                base: 'bg-[url(/assets/sportpaleis/newsletter/background.jpg)]',
            },
            'afas-dome': {
                base: 'bg-[url(/assets/afas-dome/newsletter/background.jpg)]',
            },
            'lotto-arena': {
                base: 'bg-[url(/assets/lotto-arena/newsletter/background.jpg)]',
            },
            'forest-national': {
                base: 'bg-[url(/assets/forest-national/newsletter/background.jpg)]',
            },
            'trixxo-arena': {
                base: 'bg-[url(/assets/trixxo-arena/newsletter/background.jpg)]',
            },
            'stadsschouwburg-antwerp': {
                base: 'bg-[url(/assets/stadsschouwburg-antwerp/newsletter/background.jpg)]',
            },
            'capitole-ghent': {
                base: 'bg-[url(/assets/capitole-ghent/newsletter/background.jpg)]',
            },
            'trixxo-theater': {
                base: 'bg-[url(/assets/trixxo-theater/newsletter/background.jpg)]',
            },
            'beat-tickets': {
                base: 'bg-[url(/assets/trixxo-theater/newsletter/background.jpg)]',
            },
            'channel-business': {
                base: 'bg-[url(/assets/beat/newsletter/background.jpg)]',
            },
            'channel-corporate': {
                base: 'bg-[url(/assets/beat/newsletter/background.jpg)]',
            },
        },
        showForm: {
            false: {},
            true: {
                base: 'max-lg:bg-none max-lg:bg-gray-900 max-lg:border-0',
                logo: 'hidden lg:block',
            },
        },
    },
    defaultVariants: {
        venueType: 'sportpaleis',
        hideForm: false,
    },
});

export const Newsletter = ({
    className,
    venue,
    onSubmit,
    customTranslation,
    customTranslationMessage,
    customTranslationPlaceholder,
    showForm,
    testId = 'newsletter-banner',
    ...props
}: NewsletterProps): JSX.Element => {
    const { t } = useTranslation();

    const {
        base,
        logo: logoStyles,
        heading,
        form,
        container,
    } = NewsletterStyles({ showForm });

    return (
        <div
            className={base({ className, venueType: venue })}
            {...props}
            data-testid={testId}
        >
            <Container className={container()}>
                <Logo className={logoStyles()} venueId={venue} />
                {showForm && (
                    <div>
                        <Heading as="h3" className={heading()}>
                            {customTranslation
                                ? customTranslationMessage
                                : t('newsletterMessage', { ns: 'global' })}
                        </Heading>
                        <form
                            onSubmit={(e) => {
                                const formData = new FormData(e.currentTarget);
                                const email = formData.get(
                                    'newsletterEmail'
                                ) as string;
                                onSubmit(email);
                            }}
                        >
                            <InputField
                                className={form()}
                                icon={<ChevronRightSolidIcon />}
                                label={
                                    t('newsLetterLabel', {
                                        ns: 'global',
                                    }) || 'newsletter'
                                }
                                name="newsletterEmail"
                                placeholder={
                                    customTranslation
                                        ? customTranslationPlaceholder
                                        : t('inputPlaceholders.newsletter', {
                                              ns: 'global',
                                          }) || ''
                                }
                                type="email"
                            />
                        </form>
                    </div>
                )}
            </Container>
        </div>
    );
};
